import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { selectEssSwitch } from 'shared/slice/essSwitchSlice';
import { selectTesSwitch } from 'shared/slice/tesSwitchSlice';
import { selectTgsSwitch } from 'shared/slice/tgsSwitchSlice';
import { colors } from 'shared/styles/theme';

import StatGaugeCard from './StatGaugeCard';
import { selectUnits } from 'shared/slice/unitsSlice';
import { useMemo } from 'react';
import { convertCelsiusToFahrenheit } from 'shared/helpers/helpers';


const RANGES = {
  energy: { min: 0, max: 50 },
  enclosure: { min: 0, max: 120 },
  outside: { min: -20, max: 60 },
  hours: { min: 0, max: 8760 },
};

const ratioOf = (value, { min, max }) =>
  typeof value === 'number'
    ? Math.max(0, Math.min(1, (value - min) / (max - min)))
    : 0;

const ICONS = {
  energy: '/images/stats-figma/stat-energy.png',
  enclosure: '/images/stats-figma/stat-enclosure.png',
  outside: '/images/stats-figma/stat-outside.png',
  hours: '/images/stats-figma/stat-hours.png',
};

const DetailDisplayBoxWrapper = ({ swtName, location, machine }) => {
  const unitsStatus = useSelector(selectUnits);
  const { isF } = unitsStatus;
  const { flatTgsSwitch,flatEssSwitch,flatTesSwitch } = useSelector(
    swtName === 'ess'
      ? selectEssSwitch
      : swtName === 'tgs'
      ? selectTgsSwitch
      : selectTesSwitch
  );

  const switchStatus = swtName === 'ess' ? flatEssSwitch : swtName === 'tgs' ? flatTgsSwitch : flatTesSwitch;

  const deviceData = switchStatus[location][machine];

  const instantHeatReady = useMemo(() => {
    return deviceData.instantHeat.isReady;
  }, [deviceData.instantHeat.isReady]);

  const optionalConstantReady = useMemo(() => {
    return deviceData.optionalConstantTemp.isReady;
  }, [deviceData.optionalConstantTemp.isReady]);

  const heatingScheduleActivated = useMemo(() => {
    return deviceData.heatingSchedule.isActivated;
  }, [deviceData.heatingSchedule.isActivated]);

  const snowSensorReady = useMemo(() => {
    return deviceData.snowSensor.isReady;
  }, [deviceData.snowSensor.isReady]);

  const snowSensorActivated = useMemo(() => {
    return deviceData.snowSensor.isActivated;
  }, [deviceData.snowSensor.isActivated]);

  const windFactorReady = useMemo(() => {
    return deviceData.windFactor.isReady;
  }, [deviceData.windFactor.isReady]);

  const windFactorActivated = useMemo(() => {
    return deviceData.windFactor.isActivated;
  }, [deviceData.windFactor.isActivated]);

  const consumption = useMemo(() => {
    const consumption = swtName === 'tgs' ?
      (instantHeatReady || heatingScheduleActivated || (snowSensorReady && snowSensorActivated) || (windFactorReady && windFactorActivated)) ?
        Math.round((isF ? deviceData.consumption : deviceData.consumption * 0.0283168) * 10) / 10
        : '--'
      : (instantHeatReady || optionalConstantReady || heatingScheduleActivated || (snowSensorReady && snowSensorActivated) || (windFactorReady && windFactorActivated)) ?
        Math.round(deviceData.consumption * 10) / 10
        : '--';
    return consumption;
  }, [
    instantHeatReady,
    optionalConstantReady,
    heatingScheduleActivated,
    snowSensorReady,
    snowSensorActivated,
    windFactorReady,
    windFactorActivated,
    isF,
    deviceData.consumption
  ]);

  const enclosureTemp = useMemo(() => {
    const enclosureTemp = isF ? convertCelsiusToFahrenheit(deviceData.enclosureTemp) : deviceData.enclosureTemp;
    return enclosureTemp ?? '--';
  }, [isF, deviceData.enclosureTemp]);

  const outSideTemp = useMemo(() => {
    const outSideTemp = isF ? convertCelsiusToFahrenheit(deviceData.outSideTemp) : deviceData.outSideTemp;
    return outSideTemp ?? '--';
  }, [isF, deviceData.outSideTemp]);

  const hoursOfUsage = useMemo(() => {
    return deviceData.hoursOfUsage ?? '--';
  }, [deviceData.hoursOfUsage]);

  return (
    <Wrapper>
      <Row>
        <StatGaugeCard
          title={swtName !== 'tgs' ? 'energy consumption' : 'gas consumption'}
          value={consumption}
          unit={swtName === 'tgs' ? (isF ? 'FT³/H' : 'M³/H') : 'KW/H'}
          icon={ICONS.energy}
          ratio={ratioOf(deviceData.consumption, RANGES.energy)}
        />
        <StatGaugeCard
          title={'enclosure temperature'}
          value={enclosureTemp}
          unit={isF ? '°F' : '°C'}
          icon={ICONS.enclosure}
          ratio={ratioOf(deviceData.enclosureTemp, RANGES.enclosure)}
        />
        <StatGaugeCard
          title={'outside temperature'}
          value={outSideTemp}
          unit={isF ? '°F' : '°C'}
          icon={ICONS.outside}
          ratio={ratioOf(deviceData.outSideTemp, RANGES.outside)}
        />
        <StatGaugeCard
          title={'hours of usage'}
          value={hoursOfUsage}
          unit='hrs'
          icon={ICONS.hours}
          ratio={ratioOf(deviceData.hoursOfUsage, RANGES.hours)}
        />
      </Row>
    </Wrapper>
  );
};

export default DetailDisplayBoxWrapper;

const Wrapper = styled.div`
  width: 742px;
  border-radius: 16px;

  background: ${colors.navy900};
  border: 0.8px solid rgba(29, 41, 61, 0.8);

  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px 0;
`;
const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;
